import { EventEmitter, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CustomerService } from '../customer.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MODAL, ModalService } from '../modal.service';
import { DatasService } from '../datas.service';
import { CurrentService } from '../current.service';
import { Subscription } from 'rxjs';
import { PrintService } from '../print.service';
import { CONFIG_CONTEXT } from '../../enum/config-context.enum';
import { getAppGtmRef, getHbmAppName, getHbmTotalSteps, isKitchen } from '../helpers/app-context-helper';
import { GTM } from '../gtm.service';
import { KitchenService } from '../configurators-2d/applications/kitchen.service';
import { HbmApiService } from './hbm-api.service';
import { StorageService } from '../configurators-2d/applications/storage.service';
import { BathroomHbmService } from '../configurators-2d/applications/bathroom-hbm.service';
import { KitchenConfigurationData } from '../../stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { Subscribable } from '../../stores/tunnel/model/subscribable';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import { StorageConfigurationData } from '../../stores/tunnel/model/configuration-data/storage-configuration-data';
import { BathroomHbmConfigurationData } from '../../stores/tunnel/model/configuration-data/bathroom-hbm-configuration-data';
import { logError } from '../helpers/sentry.helper';
import { IframeEvent, IframeMessage } from './types';
import { BomDataHelper } from '../helpers/bom-data-helper';

@Injectable({
  providedIn: 'root',
})
export class HbmIframeService extends Subscribable {
  private iframe?: HTMLIFrameElement | null;
  public token: string;
  public projectToInit: any;
  public iframeParametersAtInit: any;
  private readonly _plan2DrenderingEventEmitter: EventEmitter<any>;
  private _plan2DRequested: boolean;
  private _saveForPrintRequested: boolean;
  private _projectBaseUuid: string;
  private _showInspirationTip = false;
  private _showLegacyTip = false;
  private _reloadBom: boolean;
  private _initialBom: any = null;
  private _currentConfigurationData: Map<CONFIG_CONTEXT, unknown> = new Map<CONFIG_CONTEXT, unknown>();
  private _destroyed = false;
  private readonly _outbox: Array<{ event: string; data?: any }> = [];
  private _isHbmComponentActive = false;

  constructor(
    private readonly current: CurrentService,
    private readonly customer: CustomerService,
    private readonly data: DatasService,
    private readonly modalService: ModalService,
    private readonly printService: PrintService,
    private readonly http: HttpClient,
    private readonly gtm: GTM,
    private readonly router: Router,
    private readonly hbmService: HbmApiService,
    private readonly kitchenService: KitchenService,
    private readonly storageService: StorageService,
    private readonly bathroomHbmService: BathroomHbmService,
    private readonly tunnelDataStore: TunnelDataStore
  ) {
    super();

    this.iframeParametersAtInit = this.getByMeDefaultSettings();
    this._reloadBom = false;
    this._plan2DRequested = false;
    this._plan2DrenderingEventEmitter = new EventEmitter();

    this.modalService.closeEvents.subscribe((e: any) => {
      if (this.printService.hasContent()) {
        if (this.iframe) this.iframe.style.paddingBottom = '1px';
        globalThis.setTimeout(() => {
          this.printService.reset();
          if (this.iframe) this.iframe.style.paddingBottom = '0';
        }, 50);
      }
    });

    this.customer.events.subscribe((e: any) => {
      if (e.type === 'CUSTOMER_TOKEN_ACQUIRED' && this._isHbmComponentActive) {
        this.postMessageToIFrame('Login', e.data);
      }
    });

    super.subscribe();
  }

  public dispose() {
    if (this._destroyed) return;
    super.unsubscribe();
    this.iframe = null;
    this._destroyed = true;
  }

  protected subscribeToObservables(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this._currentConfigurationData = configurationData;
        }
      })
    );
  }

  attachIframe(iframe: HTMLIFrameElement) {
    this.iframe = iframe;
  }

  detachIframe() {
    this.iframe = null;
  }

  setComponentState(isActive: boolean) {
    this._isHbmComponentActive = isActive;
  }

  buildInitialUrl(): string {
    let localeParameters = '?ln=fr-FR';
    if (this.current.HBMProject) {
      localeParameters += '&projectId=' + this.current.HBMProject.uuid;
    } else if (this._projectBaseUuid) {
      localeParameters += '&projectId=' + this._projectBaseUuid;
      this._projectBaseUuid = null; // usage unique
    }

    return environment.byMe[this.customer.context].origin +
      environment.byMe[this.customer.context].appURL +
      localeParameters;
  }

  prepareBymeSettingsForLoadingStorageInspiration(projectBaseUuid: string) {
    this.iframeParametersAtInit = this.getByMeDefaultSettings();
    this._showInspirationTip = true;
    this.iframeParametersAtInit.designFromScratch = false;
    this._projectBaseUuid = projectBaseUuid;
  }

  prepareBymeSettingsForLoadingProject() {
    this.iframeParametersAtInit = this.getByMeDefaultSettings();
    this.iframeParametersAtInit.designFromScratch = false;
    this.iframeParametersAtInit.name = this.current.HBMProject.name;
    this._showLegacyTip = this.current.HBMProject.isLegacy;
  }

  prepareKitchenBymeSettings(
    modelId: number,
    layoutRef: string,
    ovenConfigurationRef: string,
    fridgeConfigurationRef: string
  ) {
    this.current.HBMProject = null;

    this.iframeParametersAtInit = this.getByMeDefaultSettings();
    this.iframeParametersAtInit.designFromScratch = false;

    if (modelId > 0) {
      let _model = this.data.models.find(m => m.id === modelId);
      for (const key in _model.style) {
        this.iframeParametersAtInit[key] = _model.style[key];
      }
    }

    if (layoutRef) {
      this.iframeParametersAtInit.templateTags = this.iframeParametersAtInit.templateTags.filter((tag: any) => {
        return tag.required[0] == layoutRef;
      });
    }

    if (fridgeConfigurationRef) {
      for (const tag of this.iframeParametersAtInit.templateTags as any[]) {
        tag.required.splice(1, 0, fridgeConfigurationRef);
      }
    }

    if (ovenConfigurationRef) {
      for (const tag of this.iframeParametersAtInit.templateTags as any[]) {
        tag.required.splice(1, 0, ovenConfigurationRef);
      }
    }
  }

  async getAuthParams(forceRenew: boolean = false): Promise<any> {
    const distributionID = environment.byMe[this.customer.context].distribID;

    if (this.customer.token) {
      if (forceRenew) {
        await this.hbmService.acquireTokenWS(this.customer.userDatas?.id, 'token', true, false);
      }

      return { token: this.customer.token, distributionID };
    }

    await this.getTokenLegal();
    return { token: this.token, distributionID };
  }

  getTokenLegal(): Promise<void> {
    return new Promise((resolve, reject) => {
      const postData: any = {
        hostName: environment.byMe.apiHostName,
        service: 'token',
      };

      this.http.post(environment.byMe.tokenURL, postData).subscribe({
        next: (responseDatas: any) => {
          this.token = responseDatas.token;
          resolve();
        },
        error: (error: any) => {
          logError(error);
          reject(error);
        }
      });
    });
  }

  onIFrameReady() {
    if (!this.iframe) return;

    this.postSettingsToIFrame();

    if (this.customer.isLogged) {
      this.postMessageToIFrame('Login', {
        token: this.customer.token,
        distributionID: environment.byMe[this.customer.context].distribID,
        additionalInfo: null,
      });
    }

    this.flushOutbox();

    if (this._showInspirationTip) {
      this._showInspirationTip = false;
      this.modalService.display(MODAL.INSPIRATION_TIP);
    } else if (this._showLegacyTip) {
      this._showLegacyTip = false;
      this.modalService.display(MODAL.MESSAGE, {
        titleRef: 'popins.legacyProject.title',
        contentRef: 'popins.legacyProject.text',
        buttons: [
          {
            labelRef: 'popins.legacyProject.confirm',
            primary: true,
            action: () => this.modalService.close(),
          },
        ],
      });
    } else if (this.current.HBMProject?.locked) {
      this.modalService.display(MODAL.MESSAGE, {
        titleRef: 'popins.lockedProject.title',
        contentRef: 'popins.lockedProject.text',
        buttons: [
          {
            labelRef: 'popins.lockedProject.confirm',
            primary: true,
            action: () => this.modalService.close(),
          },
        ],
      });
    }
  }

  postSettingsToIFrame() {
    if (this.projectToInit) {
      this.projectToInit = null;
    }

    this.postMessageToIFrame('SetUpSettings', this.iframeParametersAtInit);
  }


  isBomChanged(newBom: any): boolean {
    if (!this._initialBom?.bom?.products) {
      return true;
    }
    return !this.isProductsEqual(this._initialBom?.bom?.products, newBom.products);
  }

  // Génère un dictionnaire sur l'id des produits et la quantité présente dans la BOM
  private productsToCountMap(p: Array<{ dbID: string }>): Map<string, number> {
    const m = new Map<string, number>();
    for (const item of p ?? []) m.set(item.dbID, (m.get(item.dbID) ?? 0) + 1);
    return m;
  }

  isProductsEqual(p1: any[], p2: any[]): boolean {
    const m1 = this.productsToCountMap(p1);
    const m2 = this.productsToCountMap(p2);
    if (m1.size !== m2.size) return false;
    // comparaison des quantités
    for (const [k, v] of m1) if (m2.get(k) !== v) return false;
    return true;
  }

  postMessageToIFrame(event: string, data?: any) {
    if (!this.iframe?.contentWindow) {
      console.log('Message ' + event + ' mis en queue...');
      this._outbox.push({ event, data });
      return;
    }

    try {
      console.log('%cwindow.postMessageToIFrame()', 'background-color:green;color:white', event, data);
      this.iframe.contentWindow.postMessage(
        {
          event,
          content: data,
        },
        environment.byMe[this.customer.context].origin
      );
    } catch (err) {
      logError(err);
      this._outbox.push({ event, data });
    }
  }

  private flushOutbox() {
    if (!this.iframe?.contentWindow) return;
    while (this._outbox.length) {
      const msg = this._outbox.shift();
      this.postMessageToIFrame(msg.event, msg.data);
    }
  }

  handlePrint() {
    if (this.customer.isLogged && this.current.HBMProject) {
      this.generateSnapshotAndPlans2d(true).then(() => {
        this.modalService.display(MODAL.PRINT_MEDIAS, {});
      });
    } else {
      this._saveForPrintRequested = true;
      this.postMessageToIFrame('SaveRequested', {
        from: 'bryo',
        action: 'print',
      });
    }
  }

  generatePlans2D(withMeasure: boolean) {
    let _planTypes = ['Top', 'Face', 'Worktop', 'WallPanel'];
    let _configs = [];

    for (const planType of _planTypes) {
      _configs.push({
        typePlan: planType,
        showExteriors: 1,
        showFurniture: 1,
        showFurnitureDimension: withMeasure ? 1 : 0,
        showWallDimension: withMeasure ? 1 : 0,
        showNumbering: 1,
        showCutouts: 1,
        showFronts: 0,
        roomsOnly: 1,
        resol: 300,
        scale: 20,
        imageWidth: 2244,
        imageHeight: 1772,
      });
    }

    _configs.push({
      typePlan: 'Face',
      showExteriors: 1,
      showFurniture: 1,
      showFurnitureDimension: withMeasure ? 1 : 0,
      showWallDimension: withMeasure ? 1 : 0,
      showNumbering: 1,
      showCutouts: 1,
      showFronts: 1,
      roomsOnly: 1,
      resol: 300,
      scale: 20,
      imageWidth: 2244,
      imageHeight: 1772,
    });

    this._plan2DRequested = true;
    this.postMessageToIFrame('Compute2DPlans', {
      version: 1,
      saveWithProject: 1,
      configs: _configs,
    });
  }

  generateSnapshot() {
    this.postMessageToIFrame('SnapshotRequested', {});
  }

  generateSnapshotAndPlans2d(withMeasure: boolean): Promise<void> {
    return new Promise((resolve) => {
      this.modalService.isLoading = true;
      let _s: Subscription = this._plan2DrenderingEventEmitter.subscribe(() => {
        this.modalService.isLoading = false;
        this._plan2DRequested = false;
        resolve();
        _s.unsubscribe();
      });

      this.generateSnapshot();
      this.generatePlans2D(withMeasure);
    });
  }

  async createKitchenConfig(resetBom: boolean = true): Promise<void> {
    if (this._plan2DRequested) return;

    const initialData: KitchenConfigurationData = {
      HBMConfigurationId: this.current.HBMProject.uuid,
      projectTitle: this.current.HBMProject.name,
      bom: resetBom
        ? '{}'
        : (this._currentConfigurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData).bom,
      configurationId: undefined,
    };

    try {
      this.modalService.isLoading = true;
      await this.kitchenService.createKitchen(initialData);
    } catch (error) {
      logError(error);
    } finally {
      this.modalService.isLoading = false;
    }
  }

  async createStorageConfig(resetBom: boolean = true): Promise<void> {
    const initialData: StorageConfigurationData = {
      HBMConfigurationId: this.current.HBMProject.uuid,
      projectTitle: this.current.HBMProject.name,
      bom: resetBom
        ? '{}'
        : (this._currentConfigurationData.get(CONFIG_CONTEXT.STORAGE) as StorageConfigurationData).bom,
      configurationId: undefined,
    };

    try {
      this.modalService.isLoading = true;
      await this.storageService.createStorage(initialData);
    } catch (error) {
      logError(error);
    } finally {
      this.modalService.isLoading = false;
    }
  }

  /**
   * Crée le tunnel SDB ByMe/HBM 3D à partir de la BOM renvoyée par l'iframe,
   * afin que le back puisse chiffrer les prestations de pose (SPID).
   */
  async createBathroomHbmConfig(resetBom: boolean = true): Promise<void> {
    const initialData: BathroomHbmConfigurationData = {
      HBMConfigurationId: this.current.HBMProject.uuid,
      projectTitle: this.current.HBMProject.name,
      bom: resetBom
        ? '{}'
        : (this._currentConfigurationData.get(CONFIG_CONTEXT.BATHROOM) as BathroomHbmConfigurationData).bom,
      configurationId: undefined,
    };

    try {
      this.modalService.isLoading = true;
      await this.bathroomHbmService.createBathroomHbm(initialData);
    } catch (error) {
      logError(error);
    } finally {
      this.modalService.isLoading = false;
    }
  }

  checkForLoggedCustomer(): Promise<void> {
    return new Promise((resolve) => {
      if (this.customer.isLogged) {
        resolve();
      } else if (environment.vendor) {
        this.modalService.display(MODAL.SEARCH_CUSTOMER, {
          closeAfterSelect: true,
          onCustomerSelect: () => {
            this.modalService.close();
            resolve();
          },
        });
      } else {
        this.modalService.display(MODAL.LOGIN, {
          loginCallBack: () => {
            this.modalService.close();
            resolve();
          },
        });
      }
    });
  }

  private readonly onAuthenticationRequired = async (_: IframeMessage) => {
    try {
      const authParams = await this.getAuthParams();
      this.postMessageToIFrame('Authentication', authParams);
    } catch {
      this.postMessageToIFrame('AuthenticationFailed');
    }
  }

  private readonly onChangeNeedMapping = (_: IframeMessage) => {
    switch (this.customer.context) {
      case CONFIG_CONTEXT.KITCHEN:
        this.router.navigate(['/cuisine']);
        break;
      case CONFIG_CONTEXT.STORAGE:
        this.router.navigate(['/rangements']);
        break;
      case CONFIG_CONTEXT.BATHROOM:
        this.router.navigate(['/salle-de-bain']);
        break;
    }
  }

  private readonly onCloseApp = (_: IframeMessage) => {
    this.router.navigate(['/']);
    this.current.reset();
  }

  private readonly onAnalyticsAppStep = (msg: IframeMessage<{ step: string }>) => {
    const stepKey = msg.content?.step;
    if (!stepKey) return;

    let step = 0;
    let stepName = '';
    const nbSteps = getHbmTotalSteps(this.customer.context);

    switch (stepKey) {
      case 'step1': {
        if (isKitchen(this.customer.context)) {
          step = nbSteps - 4;
        } else {
          step = nbSteps - 3;
        }
        stepName = 'plan';
        break;
      }
      case 'step2': {
        step = nbSteps - 3;
        stepName = 'agencement';
        break;
      }
      case 'step3': {
        this._reloadBom = true;
        step = nbSteps - 2;
        stepName = 'conception';
        break;
      }
      case 'step4': {
        step = nbSteps - 1;
        stepName = 'finaliser';
        break;
      }
    }

    this.gtm.trackPage(
      `${this.router.url}/${stepName}`,
      `${getHbmAppName(this.customer.context)} | Configuration`,
      {
        stepNumber: step,
        totalSteps: nbSteps,
      }
    );
  }

  private readonly onNeedHelpClicked = (_: IframeMessage) => {
    this.modalService.display(MODAL.CONTACT);
  }

  private readonly onLoginRequired = async (_: IframeMessage) => {
    await this.checkForLoggedCustomer();
  }

  private readonly onPlans2DStop = (_: IframeMessage) => {
    this._plan2DrenderingEventEmitter.emit();
  }

  private readonly onPriceInfoButtonClicked = (msg: IframeMessage<any>) => {
    this.modalService.display(MODAL.PRICE_INFOS, {
      bom: msg.content,
      projectName: this.current.HBMProject ? this.current.HBMProject.name : null,
    });
  }

  private readonly onPrintButtonClicked = async (_: IframeMessage) => {
    if (this.customer.isLogged && this.current.HBMProject) {
      await this.generateSnapshotAndPlans2d(true);
      this.modalService.display(MODAL.PRINT_MEDIAS, {});
    } else {
      await this.checkForLoggedCustomer();
      this.postMessageToIFrame('SaveRequested', { action: 'print' });
    }
  }

  private readonly onProductListWithBOM = async (msg: IframeMessage<any>) => {
    if (this.isHbmTunnelContext() && BomDataHelper.isBomUnavailableForProductList(msg.content)) {
      this.handleUnavailableProductListBom(msg.content);
      return;
    }

    this._reloadBom = this.isBomChanged(msg.content);

    if (environment.vendor && this.isHbmTunnelContext()) {
      // Si on est dans kitchen et qu'on passe à la liste de produit, alors on veut que les plans soit générées.
      // Il faudrait faire une condition pour vérifier si les plans sont déjà générés
      this.modalService.display(MODAL.PRODUCTS_LIST_V2, {
        bom: msg.content,
        reloadBom: this._reloadBom,
      });
      this._reloadBom = false;
    } else {
      this.modalService.display(MODAL.PRODUCTS_LIST, {
        bom: msg.content,
      });
    }
  }

  private isHbmTunnelContext(): boolean {
    return (
      this.customer.context === CONFIG_CONTEXT.KITCHEN ||
      this.customer.context === CONFIG_CONTEXT.STORAGE ||
      this.customer.context === CONFIG_CONTEXT.BATHROOM
    );
  }

  private handleUnavailableProductListBom(bom: any): void {
    this._reloadBom = false;

    this.tunnelDataStore.updateConfigurationData(this.customer.context as CONFIG_CONTEXT, {
      bom: '{}',
    });
    this.tunnelDataStore.resetCurrentInstallation();
    this.tunnelDataStore.resetCurrentPricing();
    this.tunnelDataStore.resetCurrentFunding();

    this.modalService.display(MODAL.MESSAGE, {
      titleRef: 'product-list.empty-bom-title',
      contentRef: BomDataHelper.hasUnavailablePrice(bom)
        ? 'product-list.unavailable-price-error'
        : 'product-list.empty-bom-error',
      buttons: [
        {
          labelRef: 'generic.close',
          primary: true,
          action: () => this.modalService.close(),
        },
      ],
    });
  }

  private readonly onProjectSavedRequestForPrint = (_: IframeMessage) => {
    this._saveForPrintRequested = true;
  }

  private readonly onBOMComputationReady = (msg: IframeMessage<any>) => {
    this._initialBom = msg.content;
  }

  private readonly onReady = (_: IframeMessage) => {
    this.onIFrameReady();
  }

  private readonly onSaveStarted = (_: IframeMessage) => {
    const base: any = {
      projectType: getAppGtmRef(this.customer.context)
    };

    if (this.current.HBMProject) {
      base.projectId = this.current.HBMProject.id;
    }

    this.gtm.trackEvent('enregistrer-projet', base);
  }

  private readonly onSaveSucceeded = async (msg: IframeMessage<any>) => {
    this.current.HBMProject = msg.content;
    this.current.HBMProject.uuid = this.current.HBMProject.id; // crapy but satisfying patch !
    this._reloadBom = true;

    this.gtm.trackEvent('enregistrer-projet', {
      projectType: getAppGtmRef(this.customer.context),
      projectId: this.current.HBMProject.id,
    });

    if (this._saveForPrintRequested) {
      this._saveForPrintRequested = false;
      await this.generateSnapshotAndPlans2d(true);
      this.modalService.display(MODAL.PRINT_MEDIAS, {});
    }

    if (this.customer.context === CONFIG_CONTEXT.KITCHEN && this.current.vendor) {
      await this.createKitchenConfig(!this._plan2DRequested);
    }

    if (this.customer.context === CONFIG_CONTEXT.STORAGE && this.current.vendor) {
      await this.createStorageConfig(!this._plan2DRequested);
    }

    if (this.customer.context === CONFIG_CONTEXT.BATHROOM && this.current.vendor) {
      await this.createBathroomHbmConfig(!this._plan2DRequested);
    }
  }

  private readonly onTokenExpired = async (_: IframeMessage) => {
    try {
      const authParams = await this.getAuthParams(true);
      this.postMessageToIFrame('TokenRefreshed', authParams);
    } catch {
      this.postMessageToIFrame('AuthenticationFailed');
    }
  }

  private readonly handlerMap: Record<IframeEvent, (msg: IframeMessage<any>) => Promise<void> | void> = {
    AuthenticationRequired: this.onAuthenticationRequired,
    AuthenticationSucceeded: () => { },
    ChangeNeedMapping: this.onChangeNeedMapping,
    CloseApp: this.onCloseApp,
    AnalyticsAppStep: this.onAnalyticsAppStep,
    NeedHelpClicked: this.onNeedHelpClicked,
    ComposerPriceInfoButtonClicked: () => { },
    LoginRequired: this.onLoginRequired,
    LoginSucceeded: () => { },
    LoginFailed: () => { },
    Plans2DStop: this.onPlans2DStop,
    PriceInfoButtonClicked: this.onPriceInfoButtonClicked,
    PrintButtonClicked: this.onPrintButtonClicked,
    ShareProjectBOM: this.onProductListWithBOM,
    ProductListWithBOM: this.onProductListWithBOM,
    "ProductListWithBOM-FormattedPrices": () => { },
    ProjectSavedRequestForPrint: this.onProjectSavedRequestForPrint,
    BOMComputationReady: this.onBOMComputationReady,
    Ready: this.onReady,
    SaveStarted: this.onSaveStarted,
    SaveSucceeded: this.onSaveSucceeded,
    Compute2DPlans: () => { },
    SnapshotGenerated: () => { },
    SpecialProductInfoRequested: () => { },
    TokenExpired: this.onTokenExpired,
    WallEdgeStripPriceInfoButtonClicked: () => { },
    WallPanelPriceInfoButtonClicked: () => { },
    WorktopPriceInfoButtonClicked: () => { },
    WorktopTypesInfoRequested: () => { }
  };

  private readonly defaultHandler = (msg: IframeMessage) => {
    console.log(msg);
  };

  handleIframeMessage = async (e: any) => {
    if (e.origin !== environment.byMe[this.customer.context]?.origin || !e.data) {
      return;
    }
    console.log('%cwindow.handleIFrameMessage()', 'background-color:purple;color:white', e.data.event, e);

    try {
      const msg = e.data as IframeMessage<any>;
      const handler = this.handlerMap[msg.event] ?? this.defaultHandler;
      await handler(msg);
    } catch (err) {
      logError(err);
    }
  };

  getByMeDefaultSettings(): any {
    return {
      designFromScratch: true,
      name: '',
      templateTags: [
        { required: ['L', 'priority'], },
        { required: ['L'], excluded: ['priority'], },
        { required: ['I', 'priority'], },
        { required: ['I'], excluded: ['priority'], },
        { required: ['II', 'priority'], },
        { required: ['II'], excluded: ['priority'], },
        { required: ['U', 'priority'], },
        { required: ['U'], excluded: ['priority'], },
      ],
      nbMaxResultPerGroup: 5,
      iframeVersion: 4,
    };
  }
}
